import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import get from 'lodash/get';
import map from 'lodash/map';
import compact from 'lodash/compact';
import some from 'lodash/some';

import SelectDay from './selectDay';
import SelectTime from './selectTime';
import {
  resetAvailableTimes,
  fetchAvailableTimes,
} from '../../../barbers/actions';
import { setStartOfWeek } from '../../actions';
import { getDays, getTimes } from '../../../barbers/selector';
import { countTotalDuration } from '../../../../common/helpers/appointments';
import { weekdaysDisabled } from '../../../../common/helpers/schedules';
import Icon, { GLYPHS } from '../../../../common/icon';
import config from '../../../../../config';
import * as propTypes from '../propTypes';

const MAX_WEEK = config.showedWeeks.to;
const MIN_WEEK = config.showedWeeks.from;

class DateTimePicker extends Component {
  constructor(props) {
    super(props);

    this.initialState = {
      expanded: false,
      unavailable: false,
    };

    this.state = this.initialState;
  }

  componentWillMount() {
    this.props.resetAvailableTimes();
    this.fetchAvailableTimes();
    this.preselectDayAndWeek();
  }

  componentWillReceiveProps(nextProps) {
    const durationChanged = this.getServicesDuration() !== this.getServicesDuration(nextProps);
    const weekChanged = !moment(this.props.startOfWeek).isSame(moment(nextProps.startOfWeek));
    const shouldFetchTimes =
      (this.props.selectedBarber !== nextProps.selectedBarber) || durationChanged || weekChanged;
    if (shouldFetchTimes) this.fetchAvailableTimes(nextProps);

    // Don't set date if there was no appointmentId and it was set after, to
    // prevent racing condition when the appointment is set on
    // AppointmentFormContainer's state
    const appointmentCondition = !this.props.appointmentId && !nextProps.appointmentId;

    const shouldSetDate = (
      nextProps.day &&
      nextProps.time &&
      (this.props.day !== nextProps.day || this.props.time !== nextProps.time) &&
      appointmentCondition
    );

    if (shouldSetDate) {
      this.props.setDate({
        day: nextProps.day,
        time: nextProps.time,
      });
    }

    if (this.isSelectedTimeUnavailable(nextProps)) {
      this.setExpanded(!!nextProps.dateTime);
      this.props.setDate({ day: nextProps.day, time: null });
      this.setTimeUnavailable(true);
    } else if (nextProps.time && nextProps.day && nextProps.dateTime) {
      this.setTimeUnavailable(false);
    }

    if (!nextProps.day && nextProps.days.length) this.selectDay(nextProps.days[0].day);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.day !== this.props.day) {
      this.setStartOfWeek(this.props.day);
    }
  }

  setExpanded(val) {
    this.setState({ expanded: val });
  }

  setTimeUnavailable(val) {
    this.setState({ unavailable: val });
  }

  setStartOfWeek(day) {
    const startOfWeek = moment(day, config.dateFormatISO).startOf('week');
    if (startOfWeek.isSame(moment(this.props.startOfWeek))) return;
    this.props.setStartOfWeek(startOfWeek);
  }

  getServicesDuration(props) {
    const { selectedDuration, selectedServices } = props || this.props;
    if (!this.isDurationSelected(props)) return null;

    return selectedDuration || countTotalDuration(selectedServices);
  }

  getErrorMessage() {
    const barberError = this.isBarberSelected() ? '' : 'Please select a barber to see times';
    const timesError = this.props.loading || this.props.times.length ? '' : 'There are no times available';
    const serviceError = this.isDurationSelected() ? '' : 'Please select a service to see times';
    return barberError || serviceError || timesError;
  }

  setTime = (time) => {
    this.props.setDate({ day: this.props.day, time });
    if (time) this.setExpanded(true);
  };

  getWeekdaysDisabled() {
    const { currentBarber } = this.props;
    return weekdaysDisabled(get(currentBarber, 'schedules'));
  }

  getDatesDisabled() {
    const { currentBarber } = this.props;
    const mapDaysOff = item => get(item, 'wholeDay') && moment.utc(get(item, 'day')).format(config.dateFormatISO);
    return compact(map(get(currentBarber, 'days_off'), mapDaysOff));
  }

  fetchAvailableTimes(props) {
    const { selectedBarber, startOfWeek, appointmentId } = props || this.props;
    if (!startOfWeek || !this.isDurationSelected(props)) return;
    const duration = this.getServicesDuration(props);
    const start = moment(startOfWeek).format(config.dateFormatISO);
    const end = moment(startOfWeek).endOf('week').format(config.dateFormatISO);

    const shouldFetch = start && end && typeof duration === 'number';
    if (shouldFetch) this.props.fetchAvailableTimes(selectedBarber, start, end, duration, appointmentId);
  }

  selectDay = (day) => {
    this.props.setDate({ day, time: this.props.time });
    this.setStartOfWeek(day);
  };

  selectDayOfWeek = (startOfWeek) => {
    const day = moment(this.props.day, config.dateFormatISO).day();
    const nextDay = startOfWeek.day(day).format(config.dateFormatISO);
    this.selectDay(nextDay);
  };

  prevWeek = (weeksToSubtract = 0) => {
    const prev = moment(this.props.startOfWeek).subtract(weeksToSubtract, 'weeks');
    this.selectDayOfWeek(prev);
  };

  nextWeek = (weeksToAdd = 0) => {
    const next = moment(this.props.startOfWeek).add(weeksToAdd, 'weeks');
    this.selectDayOfWeek(next);
  };

  isDurationSelected(props) {
    const { selectedDuration, selectedServices } = props || this.props;
    return selectedDuration || selectedServices.length > 0;
  }

  isBarberSelected(props) {
    const { selectedBarber } = props || this.props;
    return selectedBarber;
  }

  isSelectedTimeUnavailable(props = this.props) {
    const timesAreLoaded = !!props.times.length;
    const timeSelected = !!props.time;
    return timesAreLoaded && timeSelected && !some(props.times, ['time', props.time]);
  }

  preselectDayAndWeek() {
    // Preselect a day if there is no day set
    const day = this.props.day || this.props.date || moment().format(config.dateFormatISO);
    this.selectDay(day);
    this.setStartOfWeek(day);
  }

  render() {
    const {
      days,
      times,
      day,
      time,
      startOfWeek,
      dateTime,
      edit,
      timezone,
      loading,
    } = this.props;

    return (
      <div className="layout">
        <div className="layout__item">
          <div className="c-label c-label--small-green u-inline-block u-align-middle u-1/10-medium-and-up">Time</div>
          <div
            className="u-inline-block u-align-middle u-color-grey-darken-2 u-fontsize-small"
            data-automation-id="text:selectedTime"
            display-if={!!dateTime}
          >
            { moment(dateTime).tz(timezone).format(config.fullDateTimeFormat) }
          </div>
          <button
            className="c-btn c-btn--blank u-ph--"
            type="button"
            onClick={() => this.setExpanded(!!dateTime && !this.state.expanded)}
            display-if={!!dateTime && this.props.canModifyAppointments}
          >
            <Icon glyph={this.state.expanded ? GLYPHS.remove : GLYPHS.plus} className="o-icon o-icon--small" />
          </button>
          <SelectDay
            day={day}
            days={days}
            startOfWeek={startOfWeek}
            selectDay={this.selectDay}
            prevWeek={this.prevWeek}
            nextWeek={this.nextWeek}
            minDate={moment().day(0).add(MIN_WEEK, 'weeks')}
            maxDate={moment().day(0).add(MAX_WEEK, 'weeks')}
            loading={loading}
            datesMarkedDisabled={this.getDatesDisabled()}
            daysOfWeekMarkedDisabled={this.getWeekdaysDisabled()}
            display-if={this.state.expanded || !time}
          />
          <SelectTime
            day={day}
            time={time}
            setTime={this.setTime}
            edit={edit}
            times={times}
            timezone={timezone}
            errorMessage={this.getErrorMessage()}
            unavailable={this.state.unavailable}
            loading={loading}
            expanded={this.state.expanded || !time}
          />
        </div>
      </div>
    );
  }
}

DateTimePicker.propTypes = {
  timezone: PropTypes.string.isRequired,
  selectedBarber: PropTypes.string,
  currentBarber: PropTypes.object, // eslint-disable-line
  selectedServices: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line
  selectedDuration: PropTypes.number, // eslint-disable-line
  setDate: PropTypes.func.isRequired,
  day: PropTypes.string,
  time: PropTypes.string,
  dateTime: PropTypes.string,
  edit: PropTypes.bool,
  resetAvailableTimes: PropTypes.func.isRequired,
  fetchAvailableTimes: PropTypes.func.isRequired,
  days: propTypes.days.isRequired,
  times: propTypes.times.isRequired,
  loading: PropTypes.bool.isRequired,
  startOfWeek: PropTypes.oneOfType([
    PropTypes.instanceOf(moment),
    PropTypes.instanceOf(Date),
  ]).isRequired,
  setStartOfWeek: PropTypes.func.isRequired,
  appointmentId: PropTypes.string, // eslint-disable-line
  date: PropTypes.string.isRequired,
  canModifyAppointments: PropTypes.bool.isRequired,
};
DateTimePicker.defaultProps = {
  selectedBarber: null,
  selectedDuration: null,
  day: null,
  time: null,
  dateTime: null,
  edit: false,
  appointmentId: null,
};

const mapStateToProps = (state, ownProps) => ({
  shop: state.auth.shop,
  timezone: state.auth.timezone,
  barberUser: state.auth.barberUser,
  days: getDays(state),
  times: getTimes(state, ownProps.day, state.auth.timezone),
  startOfWeek: state.appointments.startOfWeek,
  date: state.appointments.date,
  loading: state.barbers.availableTimes.loading,
});

const mapDispatchToProps = {
  resetAvailableTimes,
  fetchAvailableTimes,
  setStartOfWeek,
};

export default connect(mapStateToProps, mapDispatchToProps)(DateTimePicker);
